import { useEffect, useState } from "react";

import { apiClient } from "@/services/apiClient";
import type { ConsumerCertificateRequestSource } from "@/services/userAccountantRequestsService";

/**
 * Loads the assigned professional's profile picture via an authenticated GET (private Vercel Blob URLs are not usable as raw img src).
 */
export function useAssignedProfessionalAvatarObjectUrl(
  requestId: string | undefined,
  requestSource: ConsumerCertificateRequestSource | undefined,
  storedProfileImageUrl: string | null | undefined,
): string | null {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    if (
      requestId == null ||
      requestId.trim() === "" ||
      requestSource == null ||
      storedProfileImageUrl == null ||
      storedProfileImageUrl.trim() === ""
    ) {
      setSrc(null);
      return;
    }

    const path =
      requestSource === "accountant"
        ? `/me/accountant-requests/${encodeURIComponent(requestId)}/accepted-by/avatar-image`
        : `/me/notarial-requests/${encodeURIComponent(requestId)}/accepted-by/avatar-image`;

    const controller = new AbortController();

    void (async () => {
      try {
        const res = await apiClient.get(path, {
          responseType: "blob",
          signal: controller.signal,
        });
        if (controller.signal.aborted) {
          return;
        }
        const objectUrl = URL.createObjectURL(res.data);
        setSrc(objectUrl);
      } catch {
        if (!controller.signal.aborted) {
          setSrc(null);
        }
      }
    })();

    return () => {
      controller.abort();
      setSrc((prev) => {
        if (prev != null) {
          URL.revokeObjectURL(prev);
        }
        return null;
      });
    };
  }, [requestId, requestSource, storedProfileImageUrl]);

  return src;
}
